import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  History,
  MapPin,
  Ban,
  Users,
  ArrowRight,
} from "lucide-react";
import { EditorialPage, handwritten } from "../components/editorial";
import Button from "../components/Button";
import Navbar from "../components/Navbar";
import StoryPageHero from "../components/story/StoryPageHero";
import Reveal from "../components/story/Reveal";
import Doodle from "../components/story/Doodle";
import Highlighter from "../components/story/Highlighter";

// Safety, given the story-site treatment (LL-0199).
//
// The pillar CONTENT is unchanged: it is accurate, it is mirrored by the
// /support FAQ and the home page's trust act, and it is the thing App Review
// reads. What changed is everything around it.
//
// Three things this page needed and now has:
//  1. the brand mesh and the doodle marks, so it stops reading as a different
//     website from the home page;
//  2. alternating section GROUNDS. It was cream from top to bottom, and five
//     near-identical rows on one background is what made a genuinely strong
//     argument feel like a policy document;
//  3. Reveal instead of framer-motion. This page shipped 10 elements at
//     opacity:0 in its prerendered HTML, so its content could not paint until
//     React hydrated (see components/story/Reveal.tsx for the full reasoning).
//
// The card tilt is KEPT here, unlike on the home page's step cards, and the
// distinction is deliberate: these are frame-like tiles holding a single glyph,
// closer to a sticker than to a content card, and the alternating tilt is what
// gives a five-section scroll its rhythm.

const pillars = [
  {
    Icon: ShieldCheck,
    title: "Identity, not handles",
    body: "Real first name. Real photo. No anonymous accounts, no handle-driven cosplay. Every profile is grounded in who you are, not who you'd like to seem to be online.",
  },
  {
    Icon: History,
    title: "History you can see",
    body: "Tap any host's name and see how many loops they've shown up to, how many they've hosted, and how often their attendees came back. Trust through accountability, not vibes.",
  },
  {
    Icon: MapPin,
    title: "Privacy zones",
    body: "Your home and work are hidden inside a privacy zone: anyone looking at your map sees a neighbourhood, not a pin. You decide who sees your exact location, and when.",
  },
  {
    Icon: Users,
    title: "Per-loop filters",
    body: "Organisers set who can join: age range, friends-of-friends, vouch required. Filters are shown on the card before you ask to join. You self-select in or out, no awkward rejection.",
  },
  {
    Icon: Ban,
    title: "One tap to mute, block, report",
    body: "If someone's off, you never have to see them again. Mute keeps them out of your feed quietly. Block removes you from each other's surfaces. Report sends signal to us and the community.",
  },
];

// Grounds rotate so the five primitives read as five chapters rather than one
// long list. These are the FIXED vibrant tints, not `bg-paper-tan`: that token
// is #f0efeb, a desaturated grey-beige, and at full-section width it reads as
// the same dull grey the charcoal closings did. Neutral is right for a small
// card; it is wrong for a whole chapter.
const GROUNDS = [
  "bg-cream",
  "ground-butter",
  "ground-mint",
  "ground-peach",
  "bg-cream",
];

export default function SafetyPage() {
  useEffect(() => {
    document.title = "Safety | LetsLoop";
  }, []);

  return (
    <>
      <Navbar />
      <EditorialPage>
        <StoryPageHero
          kicker="safety"
          title={
            <>
              Spontaneous only works if it&rsquo;s{" "}
              <Highlighter tone="coral">safe.</Highlighter>
            </>
          }
          lede={
            <>
              Five primitives that make trust the default, not the exception.
              None of them are bolted on: they&rsquo;re how the app is built
              from the first screen.
            </>
          }
          marks={[
            {
              name: "star",
              size: 44,
              rotate: -12,
              className: "left-[7%] top-[32%] text-salmon-dark/55",
            },
            {
              name: "loop",
              size: 62,
              rotate: 8,
              className: "right-[9%] top-[28%] text-teal/35",
            },
            {
              name: "burst",
              size: 36,
              className: "right-[24%] bottom-[14%] text-salmon/65",
            },
          ]}
        />

        {/* ── The five primitives ───────────────────────────────── */}
        {pillars.map((p, i) => (
          <section
            key={p.title}
            className={`relative w-full ${GROUNDS[i]} py-16 md:py-24 px-6 md:px-12 overflow-hidden`}
          >
            {i === 1 && (
              <Doodle
                name="squiggle"
                size={92}
                rotate={-6}
                className="absolute w-8 h-8 md:w-auto md:h-auto right-[5%] top-[14%] text-salmon/40"
              />
            )}
            {i === 3 && (
              <Doodle
                name="heart"
                size={48}
                rotate={12}
                className="absolute w-8 h-8 md:w-auto md:h-auto left-[4%] max-md:left-[5%] bottom-[14%] text-salmon-dark/40"
              />
            )}

            <Reveal y={28}>
              <div
                className={`max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center ${
                  i % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""
                }`}
              >
                <div className="md:col-span-5">
                  <div
                    className={`bg-canvas border border-hairline rounded-3xl p-10 md:p-12 shadow-[0_18px_44px_-20px_rgba(45,56,73,0.32)] transform-gpu ${
                      i % 2 === 0 ? "-rotate-2" : "rotate-2"
                    }`}
                    style={{
                      backgroundColor:
                        i % 2 === 0
                          ? "var(--color-paper-mint)"
                          : "var(--color-paper-salmon)",
                    }}
                  >
                    <p.Icon
                      className="w-20 h-20 md:w-28 md:h-28 text-ink mx-auto"
                      strokeWidth={1.3}
                    />
                    <p
                      className="text-center mt-6 text-2xl text-ink"
                      style={handwritten}
                    >
                      {`0${i + 1}`}
                    </p>
                  </div>
                </div>
                <div className="md:col-span-7">
                  <span className="font-headline text-5xl text-salmon/30 block mb-2 tabular-nums">
                    {`0${i + 1}`}
                  </span>
                  <h2
                    className="font-headline text-ink text-3xl md:text-5xl tracking-[-0.025em] mb-5 leading-[1.1]"
                    style={{ fontWeight: 700 }}
                  >
                    {p.title}
                  </h2>
                  <p className="text-lg text-ink/70 leading-relaxed max-w-[60ch]">
                    {p.body}
                  </p>
                </div>
              </div>
            </Reveal>
          </section>
        ))}

        {/* ── Report CTA, on the vibrant teal ground ─────────────── */}
        <section className="relative w-full ground-teal py-24 md:py-32 px-6 md:px-12 overflow-hidden">
          <Doodle
            name="arrow"
            size={66}
            rotate={14}
            className="absolute w-8 h-8 md:w-auto md:h-auto left-[9%] top-[22%] text-salmon/60"
          />
          <Doodle
            name="starFilled"
            size={26}
            className="absolute w-8 h-8 md:w-auto md:h-auto right-[12%] top-[28%] text-salmon/70"
          />

          <Reveal y={26}>
            <div className="max-w-3xl mx-auto text-center relative z-10">
              <p className="text-3xl text-salmon mb-3" style={handwritten}>
                keep us honest
              </p>
              <h2
                className="font-headline text-[#f9f9f7] text-3xl md:text-5xl tracking-[-0.03em] mb-6 leading-[1.14]"
                style={{ fontWeight: 700 }}
              >
                Something feels off?{" "}
                <Highlighter tone="coral">Tell us.</Highlighter>
              </h2>
              <p className="text-lg text-[#f9f9f7]/75 mb-8 max-w-xl mx-auto leading-relaxed">
                Every report is read by a human within 24 hours. We err on the
                side of users, not optics. Mail{" "}
                <a
                  href="mailto:safety@letsloop.app"
                  className="text-salmon font-semibold border-b-2 border-salmon/40 hover:border-salmon"
                >
                  safety@letsloop.app
                </a>{" "}
                and we&rsquo;ll respond.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link to="/how-it-works">
                  <Button variant="primary" size="lg">
                    See how it works
                    <ArrowRight size={16} className="ml-2 inline" />
                  </Button>
                </Link>
                <Link to="/download">
                  {/* The outline variant is `text-ink` + `border-hairline`,
                      which is dark-on-dark and therefore invisible on this
                      ground. Overridden rather than changing the shared
                      variant, since every other use of it is on a light
                      surface. */}
                  <Button
                    variant="outline"
                    size="lg"
                    className="text-[#f9f9f7] border-[#f9f9f7]/45 hover:bg-[#f9f9f7]/15"
                  >
                    Get the app
                  </Button>
                </Link>
              </div>
            </div>
          </Reveal>
        </section>
      </EditorialPage>
    </>
  );
}
